import React, { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { contactFields } from './fieldConfig';
import { ContactFormField } from '../../types';
import styles from './ContactForm.module.css';

interface ContactFormProps {
  preselectedService?: string;
}

const ContactForm: React.FC<ContactFormProps> = ({ preselectedService }) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formValid, setFormValid] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [selectValue, setSelectValue] = useState(preselectedService || '');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setFileName(file ? file.name : null);
  }, []);

  const checkFormValidity = useCallback(() => {
    if (formRef.current) {
      setFormValid(formRef.current.checkValidity());
    }
  }, []);

  const handleFileClear = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setFileName(null);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;

    setStatus('sending');
    try {
      const formData = new FormData(formRef.current);

      const response = await fetch('/api/contact.php', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();
      if (!result.success) throw new Error(result.message);

      setStatus('success');
      formRef.current.reset();
      setSelectValue('');
      setFileName(null);
      setFormValid(false);
    } catch {
      setStatus('error');
    }
  };

  const renderField = (field: ContactFormField) => {
    const commonProps = {
      id: field.name,
      name: field.name,
      required: field.required,
      className: styles.input,
    };

    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            {...commonProps}
            placeholder={field.placeholderKey ? t(field.placeholderKey) : ''}
            rows={5}
          />
        );
      case 'select':
        return (
          <div className={styles.selectWrapper}>
            <select
              {...commonProps}
              className={styles.selectInput}
              value={selectValue}
              onChange={(e) => setSelectValue(e.target.value)}
            >
              {field.options?.map((opt) => (
                <option key={opt.value} value={opt.value} disabled={opt.value === ''}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </select>
            <svg className={styles.selectIcon} width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        );
      case 'file':
        return (
          <div className={styles.fileWrapper}>
            <input
              ref={fileInputRef}
              name={field.name}
              type="file"
              accept={field.accept}
              className={styles.fileHidden}
              onChange={handleFileChange}
            />
            <button
              type="button"
              className={styles.fileButton}
              onClick={() => fileInputRef.current?.click()}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M15.75 11.25V14.25C15.75 14.6478 15.592 15.0294 15.3107 15.3107C15.0294 15.592 14.6478 15.75 14.25 15.75H3.75C3.35218 15.75 2.97064 15.592 2.68934 15.3107C2.40804 15.0294 2.25 14.6478 2.25 14.25V11.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12.75 6L9 2.25L5.25 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 2.25V11.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t('contact.form.attachment')}
            </button>
            {fileName && (
              <span className={styles.fileName}>
                {fileName}
                <button type="button" className={styles.fileClear} onClick={handleFileClear}>&times;</button>
              </span>
            )}
          </div>
        );
      default:
        return (
          <input
            {...commonProps}
            type={field.type}
            placeholder={field.placeholderKey ? t(field.placeholderKey) : ''}
          />
        );
    }
  };

  function onTurnstileSuccess() {
    if (btnRef.current) btnRef.current.disabled = false;
  }
  function onTurnstileError() {
    if (btnRef.current) btnRef.current.disabled = true;
  }
  function onTurnstileExpired() {
    if (btnRef.current) btnRef.current.disabled = true;
  }

  return (
      <form ref={formRef} onSubmit={handleSubmit} onChange={checkFormValidity} className={styles.form}>
        {contactFields.map((field) => (
            <div key={field.name} className={styles.fieldGroup}>
              <label htmlFor={field.name} className={styles.label}>
                {t(field.labelKey)}
                {field.required && <span className={styles.required}>*</span>}
              </label>
              {renderField(field)}
            </div>
        ))}

        <div className={styles.checkboxGroup}>
          <label className={styles.checkboxLabel}>
            <input type="checkbox" name="accept_terms" required className={styles.checkbox} />
            <span>
              Elfogadom az{' '}
              <a href="/docs/aszf.pdf" target="_blank" rel="noopener noreferrer" className={styles.checkboxLink}>
                Általános Szerződési Feltételeket
              </a>
            </span>
          </label>
          <label className={styles.checkboxLabel}>
            <input type="checkbox" name="accept_privacy" required className={styles.checkbox} />
            <span>
              Elfogadom az{' '}
              <a href="/docs/adatkezelesi-tajekoztato.pdf" target="_blank" rel="noopener noreferrer" className={styles.checkboxLink}>
                Adatkezelési Tájékoztatót
              </a>
            </span>
          </label>
        </div>

        <button
            type="submit"
            className={styles.submitBtn}
            disabled={!formValid || status === 'sending'}
            ref={btnRef}
        >
          {status === 'sending' ? t('contact.form.sending') : t('contact.form.submit')}
        </button>

        {status === 'success' && (
            <div className={styles.feedback + ' ' + styles.success}>
              {t('contact.form.success')}
            </div>
        )}
        {status === 'error' && (
            <div className={styles.feedback + ' ' + styles.error}>
              {t('contact.form.error')}
            </div>
        )}
        <div className="cf-turnstile"
             data-sitekey="0x4AAAAAACuO4tkpZhFwYtgx"
             data-theme="auto"
             data-size="flexible"
             data-callback={onTurnstileSuccess}
             data-error-callback={onTurnstileError}
             data-expired-callback={onTurnstileExpired}></div>
      </form>
  );
};

export default ContactForm;
